import { useEffect, useState } from "react";
import { Menu, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { NC, telLink, openWhatsApp, defaultWhatsappMessage } from "@/lib/nc";
import { AuthControls } from "@/components/AuthControls";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Why NC", href: "#why" },
  { label: "Process", href: "#process" },
  { label: "Estimate", href: "#estimate" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-border bg-background/90 backdrop-blur-md shadow-sm"
          : "border-transparent bg-background"
      }`}
    >
      <div className="container-nc flex h-[72px] items-center justify-between gap-4">
        {/* Logo */}
        <a href="#home" className="flex items-center gap-3" aria-label={NC.brand} data-testid="nav-logo">
          <img
            src="/assets/nc-mark.png"
            alt="NC – No Contractor logo"
            className="h-9 w-auto"
          />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              No Contractor
            </span>
            <span className="text-[11px] font-medium text-gold">
              Single-point responsibility
            </span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-2">
          <Button asChild variant="outlineNavy" size="sm">
            <a href={telLink} data-testid="nav-call-btn">
              <Phone /> Call
            </a>
          </Button>
          <Button
            variant="whatsapp"
            size="sm"
            onClick={() => openWhatsApp(defaultWhatsappMessage)}
            data-testid="nav-whatsapp-btn"
          >
            <MessageCircle /> WhatsApp
          </Button>
          <AuthControls />
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-btn">
                <Menu className="!size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82%] max-w-sm">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">
                Browse NC services and contact options.
              </SheetDescription>
              <div className="flex items-center gap-3 pb-6">
                <img src="/assets/nc-mark.png" alt="NC logo" className="h-8 w-auto" />
                <span className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  No Contractor
                </span>
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <SheetClose asChild key={link.href}>
                    <a
                      href={link.href}
                      className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-accent hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-6 flex flex-col gap-3">
                <AuthControls fullWidth />
                <Button
                  variant="whatsapp"
                  size="lg"
                  onClick={() => openWhatsApp(defaultWhatsappMessage)}
                >
                  <MessageCircle /> WhatsApp NC
                </Button>
                <Button asChild variant="outlineNavy" size="lg">
                  <a href={telLink}>
                    <Phone /> Call {NC.phoneDisplay}
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
